import { useTranslation } from 'react-i18next';
import { ComponentProps, useState } from 'react';
import clsx from 'clsx';

import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { getWindowLocation } from 'utils/window';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpInput } from 'new-components/DLS/OpInput/OpInput';

import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { getCloudServerRegions } from '../../helpers/getCloudServerRegions';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';

import './ResetPasswordFlow.scss';

interface ResetPasswordFlowProps {
  backToSignin(email: string): void;
}

/**
 * @component
 * @description Component that allows the user to choose a new password if a valid token is the search params
 * and gets redirected to signin page with the correct url search params when successful
 */
export const ResetPasswordFlow = ({ backToSignin }: ResetPasswordFlowProps) => {
  const { t } = useTranslation();
  const { token, emailParam, region, orgId, service } = useAltaLoginUrlParams();
  const [form] = OpForm.useForm();
  const [error, setError] = useState<string>();

  const handleBackToSignIn = () => {
    const params = new URLSearchParams({
      ...(region && { region }),
      ...(emailParam && { email: emailParam }),
      ...(orgId && { orgId }),
      ...(service && { service }),
    });

    const windowLocation = getWindowLocation();

    window.history.replaceState(
      null,
      '',
      `${windowLocation.origin}${windowLocation.pathname}?${params}`,
    );

    backToSignin(params.get('email')!);
  };

  const handleSubmit: ComponentProps<typeof OpForm>['onSubmit'] = async ({
    values,
  }) => {
    const availableRegions = await getCloudServerRegions();
    const currentRegion = availableRegions.data.find(
      (reg) => reg.env === region,
    );

    const heliumEndpoint = currentRegion?.helium?.endpoint;

    if (!heliumEndpoint) {
      setError(t('Missing required information'));
      return;
    }

    const changePasswordResponse = await fetch(
      `${heliumEndpoint}/tokens/resetPasswordTokens/${token}/use`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: values.password,
        }),
      },
    );

    if (
      changePasswordResponse.status >= 200 &&
      changePasswordResponse.status < 300
    ) {
      handleBackToSignIn();
    } else {
      const errorData = await changePasswordResponse.json();
      setError(errorData.message);
    }
  };

  return (
    <div className="reset-password">
      {error && <ErrorAlert message={error} />}
      <OpForm
        form={form}
        hasError={false}
        className={clsx({
          'reset-password__form': !error,
          'reset-password__form-error': error,
        })}
        onSubmit={handleSubmit}
        defaultButtons={false}
      >
        <OpForm.PasswordInput
          enforcePolicy
          name="password"
          className="reset-password__input"
          label={t('New password')}
          validateTrigger="onSubmit"
          rules={[
            {
              required: true,
              message: t('Password is required')!,
            },
          ]}
        />
        <OpForm.Item
          name="confirmPassword"
          label={t('Confirm password')}
          className="reset-password__input"
          validateTrigger="onSubmit"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: t('Confirm password is required')!,
            },
            {
              validator: (_: unknown, value: string) => {
                const password = form.getFieldValue('password');
                if (value && password && value !== password) {
                  return Promise.reject(new Error(t('Passwords do not match')));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <OpInput.Password className="reset-password__input" />
        </OpForm.Item>
        <OpForm.Button
          className="reset-password__submit"
          type="primary"
          htmlType="submit"
        >
          {t('Set password')}
        </OpForm.Button>
      </OpForm>
      <OpTextButton
        className="reset-password__back-button"
        onClick={handleBackToSignIn}
      >
        {t('Back to sign in')}
      </OpTextButton>
    </div>
  );
};
